import React, { useEffect, useState } from "react";
import { Zap, Loader2, CalendarClock, CheckCircle2, Clock, AlertTriangle, X, Link2, Heart, MessageCircle, Eye } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { AddonDialog } from "@/components/social/AddonDialog";

const STATUS = {
  queued: ["مجدول", "bg-primary/15 text-primary", Clock],
  awaiting_account: ["بانتظار ربط الحساب", "bg-amber-100 text-amber-700", Link2],
  published: ["نُشر", "bg-emerald-100 text-emerald-700", CheckCircle2],
  failed: ["فشل", "bg-destructive/15 text-destructive", AlertTriangle],
  manual: ["انشر يدوياً", "bg-secondary text-muted-foreground", AlertTriangle],
  skipped: ["تجاوز", "bg-secondary text-muted-foreground", X],
};
const fmt = (iso) => new Date(iso).toLocaleString("en-GB", { timeZone: "Asia/Riyadh", weekday: "short", day: "2-digit", month: "short", year: "numeric", hour: "numeric", minute: "2-digit", hour12: true }).replace(",", "").replace(/ (\d{4}) /, " $1 · ");

export const useSuperRawaj = () => {
  const [addon, setAddon] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const load = () => api.get("/addons").then((r) => setAddon(r.data.find((a) => a.id === "super_rawaj") || null)).catch(() => {}).finally(() => setLoaded(true));
  useEffect(() => { load(); }, []);
  return { addon, loaded, reload: load };
};

// طريقة النشر على مستوى الحساب (يختارها العميل في التنفيذ أو من صفحة قنوات التواصل): الافتراضي المسار اليدوي التقليدي
export const publishModeOf = (campaign, addon) => {
  if (campaign?.auto_scheduled) return "super_rawaj";
  return addon?.active && addon.publish_mode === "super_rawaj" ? "super_rawaj" : "manual";
};

// لوحة سوبر رواج داخل الحملة: عرض الإضافة أو جدولة النشر التلقائي مع حالة كل منشور
export const SuperRawajPanel = ({ campaign, onScheduled, queue: queueProp, onQueue }) => {
  const { addon, reload } = useSuperRawaj();
  const [queueState, setQueueState] = useState([]);
  const queue = queueProp ?? queueState; // الأب (صفحة الحملات) يملك الطابور حتى تبقى الحالة متزامنة مع اعتماد كل منشور
  const setQueue = (q) => { setQueueState(q); onQueue?.(q); };
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState(false);
  const navigate = useNavigate();
  const loadQueue = () => api.get(`/social/queue?campaign_id=${campaign.id}`).then((r) => setQueue(r.data)).catch(() => {});
  useEffect(() => { if (addon?.active && !queueProp) loadQueue(); }, [campaign.id, addon?.active]); // eslint-disable-line
  if (!addon) return null;

  const schedule = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/social/schedule/${campaign.id}`);
      setQueue(data.queue);
      if (data.created) onScheduled?.();
      const rest = [data.not_generated ? `${data.not_generated} غير مولّد` : "", data.not_approved ? `${data.not_approved} بانتظار اعتمادك` : ""].filter(Boolean).join("، ");
      if (!data.created) toast.info(rest ? `لا جديد للجدولة — المتبقي: ${rest} (يُجدول تلقائياً فور اعتماده)` : "كل المنشورات المعتمدة مجدولة بالفعل ✅");
      else toast.success((data.account_connected ? `تمت جدولة ${data.created} منشور بأفضل الأوقات ✅` : `تمت جدولة ${data.created} منشور — اربط حساب ${campaign.platform} لتبدأ النشر التلقائي`) + (rest ? ` · المتبقي: ${rest}` : ""));
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const cancel = async (q) => { try { await api.delete(`/social/queue/${q.id}`); loadQueue(); } catch (e) { toast.error(formatError(e.response?.data?.detail)); } };
  const mode = publishModeOf(campaign, addon);

  if (!addon.active) {
    return (
      <div className="mb-4 rounded-xl border border-accent/40 bg-accent/5 p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="super-rawaj-offer">
        <div className="flex items-start gap-2">
          <Zap className="w-5 h-5 text-accent mt-0.5 shrink-0" />
          <div>
            <div className="font-bold text-sm">سوبر رواج — انشر هذه الحملة تلقائياً بأفضل الأوقات</div>
            <div className="text-xs text-muted-foreground">نشر تلقائي + سحب الإعجابات والمشاهدات والتعليقات + لوحة نمو ومنافسين. إضافة اختيارية بـ {addon.price} ريال/شهر.</div>
          </div>
        </div>
        <Button size="sm" className="gap-1 bg-accent hover:bg-accent/90 text-accent-foreground font-bold" onClick={() => setDialog(true)} data-testid="super-rawaj-offer-btn"><Zap className="w-4 h-4" /> فعّل سوبر رواج</Button>
        {dialog && <AddonDialog addon={addon} onClose={() => setDialog(false)} onActivated={reload} />}
      </div>
    );
  }
  const queuedIds = new Set(queue.map((q) => q.post_id));
  const pending = (campaign.posts || []).filter((p) => !queuedIds.has(p.id) && !p.uploaded);
  const generated = pending.filter((p) => p.content_id).length;
  const approved = pending.filter((p) => p.content_id && p.approved).length;
  const ready = approved > 0; // ما يُعتمد يُجدول — لا انتظار لبقية الحملة
  const approveAll = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/campaigns/${campaign.id}/approve-all`);
      toast.success(data.approved ? `تم اعتماد ${data.approved} منشورًا${data.queued ? ` وجدولة ${data.queued} للنشر التلقائي ✅` : ""}` : "لا توجد منشورات مولّدة بانتظار الاعتماد");
      if (data.approved) { onScheduled?.(); loadQueue(); }
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const until = addon.expires_at ? new Date(addon.expires_at).toLocaleDateString("ar-SA-u-ca-gregory-nu-latn", { dateStyle: "medium" }) : "";
  if (mode === "manual") return null; // المسار اليدوي التقليدي — التبديل عبر مبدّل «طريقة التنفيذ» فوق الحملة
  return (
    <div className="mb-4 rounded-xl border border-primary/30 bg-primary/5 p-4" data-testid="super-rawaj-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
        <div className="flex items-center gap-2 font-bold text-sm flex-wrap">
          <Zap className="w-4 h-4 text-primary" /> سوبر رواج — النشر التلقائي ({queue.length})
          <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 flex items-center gap-1" data-testid="super-rawaj-active-badge">
            <CheckCircle2 className="w-3 h-3" /> {addon.admin ? "مفعّل — حساب المشرف" : until ? `اشتراكك مفعّل حتى ${until}` : "اشتراكك مفعّل"}
          </span>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <Button size="sm" variant="outline" className="gap-1 border-border" onClick={() => navigate("/app/social")} data-testid="super-rawaj-accounts-btn"><Link2 className="w-3.5 h-3.5" /> الحسابات</Button>
          {generated > approved && (
            <Button size="sm" className="gap-1 bg-rose-600 hover:bg-rose-700 text-white" onClick={approveAll} disabled={busy} data-testid="super-rawaj-approve-all-btn">
              <CheckCircle2 className="w-3.5 h-3.5" /> اعتمد كل المولّد ({generated - approved})
            </Button>
          )}
          <Button size="sm" className={`gap-1 font-bold text-white ${queue.length > 0 && pending.length === 0 ? "bg-emerald-600 hover:bg-emerald-700" : ready ? "bg-rose-600 hover:bg-rose-700" : ""}`}
            onClick={schedule} disabled={busy || !ready} data-testid="super-rawaj-schedule-btn">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CalendarClock className="w-3.5 h-3.5" />} {queue.length > 0 && pending.length === 0 ? "تمت الجدولة ✓" : "جدوِل النشر التلقائي"}
          </Button>
        </div>
      </div>
      {pending.length > 0 && (
        <p className={`text-xs mb-2 ${ready ? "text-emerald-400" : "text-muted-foreground"}`} data-testid="super-rawaj-readiness">
          {ready ? `${approved} منشور معتمد جاهز للجدولة ✓ (يُجدول تلقائياً فور الاعتماد)`
            : `ما يُعتمد يُجدول تلقائياً بموعده — مولّد ${generated}/${pending.length} · معتمد ${approved}/${pending.length}`}
        </p>
      )}
      {queue.length === 0 ? <p className="text-xs text-muted-foreground">يُجدوَل كل منشور معتمد في أفضل وقت لمنصة {campaign.platform}، ثم يُنشر وتُسحب أرقامه تلقائياً — دون أي رفع أو إدخال يدوي.</p> : (
        <div className="space-y-1.5 max-h-56 overflow-y-auto" data-testid="super-rawaj-queue">
          {queue.map((q) => { const [l, cls, Icon] = STATUS[q.status] || STATUS.queued; return (
            <div key={q.id} className="flex items-center justify-between gap-2 text-xs border border-border rounded-lg px-3 py-1.5 bg-background/60" data-testid={`queue-${q.id}`}>
              <span className="flex items-center gap-1.5"><span className={`px-2 py-0.5 rounded-full flex items-center gap-1 ${cls}`}><Icon className="w-3 h-3" /> {l}</span><span className="text-muted-foreground">{q.kind === "video" ? "فيديو" : "صورة"}</span></span>
              <span className="inline-flex items-center gap-1.5 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 font-semibold text-emerald-600 dark:text-emerald-400 tabular-nums whitespace-nowrap" dir="ltr" data-testid={`queue-date-${q.id}`}><CalendarClock className="w-3.5 h-3.5 shrink-0" />{fmt(q.publish_at)}</span>
              {q.metrics && <span className="flex items-center gap-1.5"><Heart className="w-3 h-3" /> {q.metrics.likes} <MessageCircle className="w-3 h-3" /> {q.metrics.comments} <Eye className="w-3 h-3" /> {q.metrics.views}</span>}
              {q.permalink && <a href={q.permalink} target="_blank" rel="noopener noreferrer" className="text-primary underline" data-testid={`queue-link-${q.id}`}>عرض المنشور</a>}
              {q.error && <span className="text-destructive truncate max-w-[200px]" title={q.error}>{q.error}</span>}
              {["queued", "awaiting_account", "failed"].includes(q.status) && <button onClick={() => cancel(q)} className="text-muted-foreground hover:text-destructive" data-testid={`queue-cancel-${q.id}`}><X className="w-3.5 h-3.5" /></button>}
            </div>
          ); })}
        </div>
      )}
    </div>
  );
};
